import { SWRFetcher } from '@commenda/utils';
import type { ChatRoomType } from '@prisma/client';
import useSWR from 'swr';
import { API_BASE_PATH } from './constants';

export interface ResponseTimeSlaItem {
  messageId: string;
  sentAt: string;
  senderName: string | null;
  senderEmail: string | null;
  companyName: string | null;
  corporationName: string | null;
  messageSnippet: string;
  chatRoomId: string;
  chatRoomType: ChatRoomType;
  serviceRequestId: string | null;
  isCommendaFiling: boolean | null;
  serviceRequestName: string | null;
  assigneeName: string | null;
  linkPath: string | null;
}

export interface ResponseTimeSlaMeta {
  total: number;
}

interface ResponseTimeSlaResponse {
  items: ResponseTimeSlaItem[];
  nextCursor?: string;
  meta: ResponseTimeSlaMeta;
}

interface ResponseTimeSlaOptions {
  thresholdHours?: number;
  lookbackDays?: number;
  search?: string;
  sortBy?: 'sentAt' | 'senderName' | 'companyName';
  sortOrder?: 'asc' | 'desc';
  cursor?: string;
  limit?: number;
  enabled?: boolean;
}

function buildResponseTimeSlaQueryString(
  options: ResponseTimeSlaOptions
): string | null {
  if (options.enabled === false) return null;
  const q = new URLSearchParams();
  if (options.thresholdHours)
    q.set('thresholdHours', String(options.thresholdHours));
  if (options.lookbackDays)
    q.set('lookbackDays', String(options.lookbackDays));
  if (options.search) q.set('search', options.search);
  if (options.sortBy) q.set('sortBy', options.sortBy);
  if (options.sortOrder) q.set('sortOrder', options.sortOrder);
  if (options.cursor) q.set('cursor', options.cursor);
  if (options.limit) q.set('limit', String(options.limit));
  const qs = q.toString();
  return qs
    ? `${API_BASE_PATH}/sla/response-time?${qs}`
    : `${API_BASE_PATH}/sla/response-time`;
}

interface ResponseTimeSlaCountResponse {
  count: number;
}

/**
 * Lightweight count of currently-unreplied threads, for the sidebar indicator. Polls every
 * 5 minutes so the badge stays reasonably fresh while the app is open.
 */
export function useResponseTimeSlaCount(options: { enabled?: boolean } = {}) {
  const key =
    options.enabled === false ? null : `${API_BASE_PATH}/sla/response-time/count`;

  const { data, error, mutate, isLoading } = useSWR<ResponseTimeSlaCountResponse>(
    key,
    key ? SWRFetcher({ method: 'GET' }, undefined) : null,
    { refreshInterval: 5 * 60 * 1000 }
  );

  return {
    count: data?.count ?? 0,
    error,
    mutate,
    isLoading: !!isLoading,
  };
}

export function useResponseTimeSla(options: ResponseTimeSlaOptions = {}) {
  const key = buildResponseTimeSlaQueryString(options);

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<ResponseTimeSlaResponse>(
      key,
      key ? SWRFetcher({ method: 'GET' }, undefined) : null,
      {
        // Refresh only on page load (mount) and explicit mutate() from the Refresh button —
        // never auto-revalidate on tab focus or network reconnect.
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    );

  return {
    data: data?.items ?? [],
    nextCursor: data?.nextCursor,
    meta: data?.meta,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
}
